import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Orders } from '../../../../../../modules/API/Orders/orders';
import useWebSocket from '../../../../../../generic/hooks/useWebSocket';
import { handleNotifications } from '../../../../../../generic/utils/handleNotifications';
import selectBrodMigdal from '../../../../../../generic/utils/selectBrodMigdal';
import { whoCanSeeOrder } from '../../helpers/orderPermissions';
import { filterByInputAndStatus } from '../../helpers/orderFilter';
import { sortByFieldDesc } from '../../helpers/orderSort';
import { ClientHash, OrderItem, OrderUser, SortableField } from '../../types';

const ORDERS_WS_URL = 'wss://my.agroline.co.il/ws/ws';

const useOrdersList = () => {
    const user = useSelector((s: any) => s.mainReducer.user) as OrderUser;
    const defaults = useSelector((s: any) => s.defaults?.defaults);
    const clientHash = (useSelector(
        (s: any) => s?.defaults?.clientList?.hashTable,
    ) as ClientHash) || ({} as ClientHash);
    const isSellerSeeAllOrders = !!selectBrodMigdal(defaults, 364, 'Logical_Value');

    const [orders, setOrders] = useState<OrderItem[]>([]);
    const [input, setInput] = useState('');
    const [statusId, setStatusId] = useState<number | null>(null);
    const [sortedValue, setSortedValue] = useState<SortableField | ''>('');
    const [isLoading, setIsLoading] = useState(false);

    const { socket, message } = useWebSocket(ORDERS_WS_URL);

    const azmanot = useMemo(() => {
        const filtered = filterByInputAndStatus(orders, input, statusId, statusId !== null);
        return sortedValue ? sortByFieldDesc(filtered, sortedValue) : filtered;
    }, [orders, input, statusId, sortedValue]);

    const loadOrders = useCallback(async (dateStart: string, dateEnd: string) => {
        setIsLoading(true);
        try {
            const res = await Orders.getOrdersShort(dateStart, dateEnd);
            const sorted = res
                .sort((a: OrderItem, b: OrderItem) => +a.Delivery_Date - +b.Delivery_Date)
                .reverse();
            setOrders(sorted);
        } catch (err: any) {
            const message = err?.response?.data?.message;
            const isJwt =
                message === 'jwt malformed' ||
                message === 'jwt expired' ||
                message === 'קונטקסט לא פעיל, כנס מחדש למערכת';
            if (isJwt) {
                handleNotifications('התנתקת מהמערכת, אנא כנס מחדש', { type: 'error' });
            }
        } finally {
            setIsLoading(false);
        }
    }, []);

    const removeOrder = useCallback(
        async (OrderID: number) => {
            try {
                const res = await Orders.deleteOrder(OrderID);
                if (res?.message) {
                    handleNotifications(res.message, { type: 'success' });
                }
                let removed: OrderItem | undefined;
                setOrders(prev => {
                    removed = prev.find(e => e.OrderID === OrderID);
                    return prev.filter(o => o.OrderID !== OrderID);
                });
                if (removed) {
                    try {
                        await Orders.passNewOrderToWS({ ...removed, Status: -1 });
                    } catch {
                        // swallow — same as legacy
                    }
                }
                Orders.logOrderStatusHistory(OrderID, user, -1);
            } catch (err: any) {
                const message = err?.response?.data?.message;
                if (message) {
                    handleNotifications(message, { type: 'error' });
                }
            }
        },
        [user],
    );

    useEffect(() => {
        let incoming: OrderItem | null = null;
        try {
            incoming = JSON.parse(message);
        } catch {
            incoming = null;
        }
        if (!incoming) return;
        const next = incoming;
        setOrders(prev => {
            const existing = prev.find(e => e.OrderID === next.OrderID);
            if (existing) {
                if (next.Status === -1) {
                    return prev.filter(e => e.OrderID !== next.OrderID);
                }
                return prev.map(e => (e.OrderID === next.OrderID ? next : e));
            }
            if (next.Status === -1) return prev;
            if (whoCanSeeOrder(user, next.CustomerID, clientHash, isSellerSeeAllOrders)) {
                return [next, ...prev];
            }
            return prev;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [message, socket]);

    return {
        azmanot,
        input,
        statusId,
        sortedValue,
        isLoading,
        user,
        loadOrders,
        setInput,
        setStatusId,
        setSortedValue,
        removeOrder,
    };
};

export default useOrdersList;
